import { groupColumnsForDisplay } from 'dashboard/utils'
import {
  AGGREGATION_LABELS,
  COUNT_TOKEN,
  dataFieldToken,
  shortColumnName
} from './data-field-tokens'
import { filterTokenOptions, isEmbeddableColumn } from './filter-fields'
import { resolveFilterValue } from './filter-interpolation'

// The two steps behind the text chart's data fields: pick a field, then pick
// what to print about it. Both the "/" menu and the toolbar dropdowns build from
// here so they can never drift.

const NUMERIC_FILTER_TYPES = ['min_max', 'min_max_percent', 'greater_than', 'time_in_seconds']
// The display_as values chart-card's getAggregationOptions treats as numeric.
const MONEY_DISPLAY_TYPES = ['money', 'two_digit_money']

// Sum and average need a number; min and max also read on a date; a percentage
// averages but does not add up. Anything else is quoted by how many different
// values it holds. A plain row count is the standalone Count field, since it is
// the same number whichever column it hangs off.
const aggregationsFor = (column) => {
  const isDate = column?.filter_type === 'date' || column?.display_as === 'date'
  const isNumeric =
    NUMERIC_FILTER_TYPES.includes(column?.filter_type) ||
    MONEY_DISPLAY_TYPES.includes(column?.display_as)

  if (isNumeric) return ['min', 'max', 'sum', 'avg']
  if (column?.display_as === 'percent') return ['min', 'max', 'avg']
  if (isDate) return ['min', 'max']
  return ['distinct']
}

// Picked at the first step, it inserts straight away: there is nothing to
// calculate about it.
export const COUNT_FIELD = { label: 'Count', value: COUNT_TOKEN, token: COUNT_TOKEN }

// Every row has its own id, so the Count field says everything this one could.
const isRowId = (column) => column.database_column_name === 'id'

/**
 * Step one: the row Count, then every column the text can quote, grouped and
 * ordered the way the chart column pickers order them.
 */
export const buildFieldOptions = (columns, getColumnDisplayName, municipality) => {
  const usable = (columns || []).filter((column) => column?.accessor_key && !isRowId(column))
  if (usable.length === 0) return []

  return [
    { label: 'Summary', options: [COUNT_FIELD] },
    ...groupColumnsForDisplay(usable, municipality).map(({ label, columns: grouped }) => ({
      label,
      options: grouped.map((column) => ({
        label: getColumnDisplayName(column, municipality),
        value: column.accessor_key,
        column
      }))
    }))
  ]
}

/**
 * Step two: what can be printed about one field. The data aggregations are
 * always available; a filter value is offered only when that filter is actually
 * set, because a token for an unfiltered column prints nothing and reads as a
 * bug. Each filter entry carries the value it would print.
 */
export const buildCalculationOptions = (column, context = {}) => {
  const { params = {}, tableColumns = [], municipalitySettings = null } = context
  const name = shortColumnName(column)

  const data = aggregationsFor(column).map((aggregation) => ({
    label: AGGREGATION_LABELS[aggregation],
    token: dataFieldToken(name, aggregation),
    value: dataFieldToken(name, aggregation)
  }))

  const filters = isEmbeddableColumn(column)
    ? filterTokenOptions(column)
        .map((option) => ({
          ...option,
          value: option.token,
          current: resolveFilterValue(option.key, params, tableColumns, municipalitySettings)
        }))
        .filter((option) => option.current !== '')
    : []

  return [
    { label: 'From the data', options: data },
    ...(filters.length > 0 ? [{ label: 'From the filter', options: filters }] : [])
  ]
}
